/**
 * Provider-agnostic weather vocabulary. PURE — no React, no fetch, no env.
 *
 * Two providers (OpenWeatherMap 2.5 and open-meteo) describe the same sky with
 * completely different code spaces, and `UseWeatherResult.conditionCode` is
 * contractually "normalized, not provider-specific". This module is the only
 * place either code space is understood; everything downstream sees a
 * `ConditionCode`.
 */

export type ConditionCode =
  | 'clear'
  | 'partly-cloudy'
  | 'cloudy'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'freezing-rain'
  | 'sleet'
  | 'snow'
  | 'thunderstorm'
  | 'wind'
  | 'unknown';

interface ConditionMeta {
  readonly label: string;
  /** Single glyph — the top bar renders `now.icon` verbatim (contract). */
  readonly day: string;
  readonly night: string;
}

const META: Readonly<Record<ConditionCode, ConditionMeta>> = {
  clear: { label: 'Clear', day: '☀️', night: '🌙' },
  'partly-cloudy': { label: 'Partly cloudy', day: '⛅', night: '☁️' },
  cloudy: { label: 'Cloudy', day: '☁️', night: '☁️' },
  fog: { label: 'Fog', day: '🌫️', night: '🌫️' },
  drizzle: { label: 'Drizzle', day: '🌦️', night: '🌧️' },
  rain: { label: 'Rain', day: '🌧️', night: '🌧️' },
  'freezing-rain': { label: 'Freezing rain', day: '🌧️', night: '🌧️' },
  sleet: { label: 'Sleet', day: '🌨️', night: '🌨️' },
  snow: { label: 'Snow', day: '❄️', night: '❄️' },
  thunderstorm: { label: 'Thunderstorm', day: '⛈️', night: '⛈️' },
  wind: { label: 'Windy', day: '💨', night: '💨' },
  unknown: { label: 'Unknown', day: '🌡️', night: '🌡️' },
};

export function conditionLabel(code: ConditionCode): string {
  return META[code].label;
}

export function conditionIcon(code: ConditionCode, isDay: boolean = true): string {
  return isDay ? META[code].day : META[code].night;
}

/** 'light rain' -> 'Light rain'. Providers send lowercase descriptions. */
export function sentenceCase(text: string): string {
  const trimmed = text.trim();
  if (trimmed === '') return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * WMO 4677 weather codes (open-meteo).
 * https://open-meteo.com/en/docs — the `weather_code` table.
 */
export function wmoToCondition(code: number): ConditionCode {
  if (!Number.isFinite(code)) return 'unknown';
  if (code === 0) return 'clear';
  if (code === 1 || code === 2) return 'partly-cloudy';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code === 56 || code === 57 || code === 66 || code === 67) return 'freezing-rain';
  if (code >= 51 && code <= 55) return 'drizzle';
  if (code >= 61 && code <= 65) return 'rain';
  if (code === 77 || (code >= 71 && code <= 75)) return 'snow';
  if (code >= 80 && code <= 82) return 'rain';
  if (code === 85 || code === 86) return 'snow';
  if (code >= 95 && code <= 99) return 'thunderstorm';
  return 'unknown';
}

/**
 * OpenWeatherMap condition IDs.
 * https://openweathermap.org/weather-conditions
 */
export function owmIdToCondition(id: number): ConditionCode {
  if (!Number.isFinite(id)) return 'unknown';
  if (id >= 200 && id < 300) return 'thunderstorm';
  if (id >= 300 && id < 400) return 'drizzle';
  if (id === 511) return 'freezing-rain';
  if (id >= 500 && id < 600) return 'rain';
  if (id >= 611 && id <= 616) return 'sleet';
  if (id >= 600 && id < 700) return 'snow';
  // 7xx is "atmosphere": mist/smoke/haze/dust/ash/squall/tornado.
  if (id === 771 || id === 781) return 'wind';
  if (id >= 700 && id < 800) return 'fog';
  if (id === 800) return 'clear';
  if (id === 801 || id === 802) return 'partly-cloudy';
  if (id === 803 || id === 804) return 'cloudy';
  return 'unknown';
}

/** OWM icon codes end in 'd' or 'n'. Defaults to day when absent. */
export function owmIconIsDay(icon: string | undefined): boolean {
  return typeof icon === 'string' ? !icon.endsWith('n') : true;
}

/* -------------------------------------------------------------- */
/* Unit conversion. The contract demands BOTH units, always, so    */
/* every provider response is normalized to Celsius and converted. */
/* -------------------------------------------------------------- */

export function cToF(c: number): number {
  return c * 1.8 + 32;
}

export function fToC(f: number): number {
  return (f - 32) / 1.8;
}
